/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { backendApi } from '@/features/apiSlice';
import { conferencerApi } from '@/features/conferencerApi';
import { genAI } from '@/features/genAiSlice';
import { projectReducer } from '@/features/projectsSlice';
import { reportsApi } from '@/features/reportsApi';

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
});

// Redux store
export const store = configureStore({
  reducer: {
    [backendApi.reducerPath]: backendApi.reducer,
    [conferencerApi.reducerPath]: conferencerApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [genAI.reducerPath]: genAI.reducer,
    currentProject: projectReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      backendApi.middleware,
      conferencerApi.middleware,
      reportsApi.middleware,
      genAI.middleware,
    ),
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
});

setupListeners(store.dispatch);

// Types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed functions
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
